import type { FunctionComponent } from 'react'
import { useState } from 'react'
import { TypographyV2 } from '@which/seatbelt'
import { ChevronDownIcon, ChevronUpIcon } from '@which/seatbelt/src/components/Icons/Navigational'
import { dynamicGa4DataLayerPush } from '@which/shared'

import classnames from 'classnames'

import { SVGUtil } from '../SVGUtil'
import { SVGName } from '../SVGUtil/svgs'
import styles from './FreeMembershipBenefitsPanel.module.scss'

const benefits: { title: string; description: string; link: string; icon: SVGName }[] = [
  {
    title: 'Expert articles',
    description:
      "Browse a selection of reviews and advice by trusted Which? experts which you've unlocked",
    link: '/free-account-unlocked',
    icon: SVGName.ArticleExpert,
  },
  {
    title: 'Sneak peek of premium reviews',
    description:
      'Discover the latest head-to-head review of two products normally reserved for paying subscribers',
    link: '/free-account-exclusive',
    icon: SVGName.ArticleSneakPeek,
  },
  {
    title: 'Save articles',
    description: 'Save your favourite articles and read them later',
    link: '/saved',
    icon: SVGName.ArticleSave,
  },
]

export type FreeMembershipBenefitsPanelProps = {
  placement?: 'mobile' | 'desktop'
  fullWidth?: boolean
  desktopBreakpoint?: 768 | 1024
}

export const FreeMembershipBenefitsPanel: FunctionComponent<FreeMembershipBenefitsPanelProps> = ({
  placement = 'desktop',
  fullWidth = false,
  desktopBreakpoint = 1024,
}) => {
  const collapsible = placement === 'mobile'
  const [isExpanded, setIsExpanded] = useState(false)
  const ChevronIcon = isExpanded ? ChevronUpIcon : ChevronDownIcon
  const benefitsListId = `free-membership-benefits-list-${placement}`

  const handleToggle = () => {
    const nextExpandedState = !isExpanded
    setIsExpanded(nextExpandedState)
    dynamicGa4DataLayerPush({
      event: 'click_exploreFAmembership_dropdown',
      action_group: 'explore your free membership',
      item_text: nextExpandedState ? 'expand' : 'collapse',
      item_group: undefined,
    })
  }

  return (
    <section
      className={classnames(styles.panel, {
        [styles.collapsible]: collapsible,
        [styles.fullWidth]: fullWidth && collapsible,
        [styles.mobilePlacement]: placement === 'mobile',
        [styles.desktopPlacement]: placement === 'desktop',
        [styles.tabletBreakpoint]: desktopBreakpoint === 768,
      })}
      data-testid="free-account-membership-panel"
      aria-label="Explore your free membership"
    >
      {collapsible ? (
        <h2>
          <button
            type="button"
            className={styles.toggle}
            aria-label="Explore your free membership"
            aria-expanded={isExpanded}
            aria-controls={benefitsListId}
            onClick={handleToggle}
          >
            <TypographyV2 tag="span" textStyle="sb-text-body-default-strong">
              Explore your free membership
            </TypographyV2>
            <span aria-hidden="true">
              <ChevronIcon width={16} height={16} className={styles.chevronIcon} />
            </span>
          </button>
        </h2>
      ) : (
        <TypographyV2 tag="h2" textStyle="sb-text-heading-small" className={styles.heading}>
          Explore your free membership
        </TypographyV2>
      )}
      <ul id={benefitsListId} className={styles.benefits} hidden={collapsible && !isExpanded}>
        {benefits.map(({ title, description, link, icon }) => (
          <li key={title} className={styles.benefit}>
            <a
              href={link}
              className={styles.benefitLink}
              aria-label={`${title}: ${description}`}
              data-which-id="explore-free-membership-card"
              data-item-text={title}
              data-item-parent-text="explore your free membership"
            >
              <span className={styles.icon} aria-hidden="true">
                <SVGUtil
                  name={icon}
                  width={44}
                  height={44}
                  viewBox="0 0 44 44"
                  title="Article icon"
                />
              </span>
              <div className={styles.textContent}>
                <TypographyV2 tag="h3" textStyle="sb-text-interface-body-small-strong">
                  {title}
                </TypographyV2>
                <TypographyV2 tag="p" textStyle="sb-text-body-small-regular">
                  {description}
                </TypographyV2>
              </div>
            </a>
          </li>
        ))}
      </ul>
    </section>
  )
}
